/**
 * Analytics constants for Rovo Spaces impact instrumentation.
 *
 * Centralised here so event names / action-subjects don't drift across call
 * sites and are easy to assert in unit tests. See the impact-metrics plan
 * (ROVOFE-2925, Atlas ATLAS-124860).
 *
 * Instrumentation is guarded by the existing `rovo_chat_spaces_experiment`
 * experiment — the same experiment that renders Spaces — inlined at each
 * `FeatureGates.getExperimentValue(...)` call site in `useSpacesAnalytics`.
 */

/**
 * Grouped Spaces analytics vocabulary. A single export keeps event names in one
 * place (one-value-export-per-file) while callers reference stable keys.
 */
export const SPACES_ANALYTICS = {
	/**
	 * `source` value used on AI MAU events so Spaces value-moments are
	 * attributable back to the Spaces surface within Rovo Chat MAU.
	 */
	mauSource: 'rovoSpace',
	/** GASv3 action / actionSubject vocabulary for Spaces track/ui events. */
	events: {
		/** A space was successfully created. */
		spaceCreated: { action: 'created', actionSubject: 'rovoSpace' },
		/** A source (Confluence/Jira/etc.) was added to a space. */
		sourceAdded: { action: 'added', actionSubject: 'spaceSource' },
		/** A source was removed from a space (net-of-adds for sources-per-space). */
		sourceRemoved: { action: 'removed', actionSubject: 'spaceSource' },
		/** Smart-context source recommendations were shown to the user (impression → CTR denominator). */
		sourceRecommendationsViewed: { action: 'viewed', actionSubject: 'spaceSourceRecommendations' },
		/** A conversation/chat was attached (pinned) to a space. */
		conversationAdded: { action: 'added', actionSubject: 'spaceConversation' },
		/** A memory was created in a space. */
		memoryCreated: { action: 'created', actionSubject: 'spaceMemory' },
		/** Space memories were retrieved/shown (retrieval signal). */
		memoriesViewed: { action: 'viewed', actionSubject: 'spaceMemory' },
		/** The user searched the spaces list. */
		spaceSearched: { action: 'searched', actionSubject: 'rovoSpace' },
		/** A starter prompt on the space landing page was clicked (which starter drove engagement). */
		starterClicked: { action: 'clicked', actionSubject: 'spaceStarter' },
	},
	/** Screen-view event names. */
	screens: {
		spaceLandingPage: 'rovoSpaceLandingPage',
	},
} as const;
